<template>
    <div>
        <div v-if="is_loading" class="text-muted py-3">
            <i class="fa fa-spinner fa-spin me-1"></i> Memuat CPPT...
        </div>

        <template v-else-if="rows.length">
            <div class="d-flex justify-content-end mb-2">
                <button
                    class="btn btn-sm btn-outline-danger"
                    :disabled="is_exporting"
                    @click="unduhPdf"
                >
                    <i :class="is_exporting ? 'fa fa-spinner fa-spin' : 'fa fa-file-pdf'" class="me-1"></i>
                    Export PDF ({{ count }} catatan)
                </button>
            </div>

            <div class="table-responsive">
                <table class="table table-bordered align-middle mb-0 cppt-table">
                    <thead class="table-light text-center">
                        <tr>
                            <th style="width: 130px">Tanggal dan Jam Pemeriksaan</th>
                            <th colspan="2">Uraian Pemeriksaan</th>
                            <th style="width: 200px">Instruksi DPJP</th>
                            <th style="width: 170px">
                                Nama &amp; Tanda Tangan DPJP / Tenaga Kesehatan
                            </th>
                            <th style="width: 110px">Unit</th>
                        </tr>
                    </thead>

                    <tbody v-for="row in rows" :key="row.IdCPPT">
                        <tr v-for="(soap, index) in uraian(row)" :key="`${row.IdCPPT}-${soap.kode}`">
                            <td v-if="index === 0" :rowspan="4" class="text-center">
                                <div>{{ formatTanggal(row.CreatedAt) }}</div>
                                <div class="small text-muted">{{ formatJam(row.CreatedAt) }}</div>
                            </td>

                            <td class="text-center fw-bold soap-kode">{{ soap.kode }}</td>
                            <td class="pre-wrap">{{ soap.isi || "" }}</td>

                            <td v-if="index === 0" :rowspan="4" class="pre-wrap align-top small">
                                {{ instruksi(row) || "" }}
                            </td>

                            <td v-if="index === 0" :rowspan="4" class="align-top small">
                                <div v-if="row.TandaTangan" class="mb-1 text-center">
                                    <img
                                        :src="'/storage/signatures/' + row.TandaTangan"
                                        alt="Tanda Tangan"
                                        style="max-width: 100px; max-height: 50px"
                                    />
                                </div>
                                <div class="fw-semibold">{{ row.SavedBy || row.name || "-" }}</div>
                                <div class="text-muted">{{ formatTanggal(row.CreatedAt) }}</div>
                                <div v-if="row.DiisiOleh" class="badge bg-light text-dark border mt-1">
                                    {{ row.DiisiOleh }}
                                </div>
                                <div v-if="row.ApproveCPPT" class="text-success small mt-1">
                                    <i class="fa fa-check-circle"></i> Read back
                                    {{ formatTanggal(row.ApproveCPPTDate) }}
                                </div>
                            </td>

                            <td v-if="index === 0" :rowspan="4" class="align-top small">
                                <div>{{ unit(row) }}</div>
                                <div v-if="row.Poli" class="text-muted">{{ row.Poli }}</div>
                                <div v-if="row.Regno" class="text-muted">No. Reg {{ row.Regno }}</div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2">
                <span class="text-muted small">
                    Halaman {{ page }} — {{ rows.length }} dari {{ count }} catatan
                </span>
                <div class="btn-group btn-group-sm">
                    <button
                        class="btn btn-outline-secondary"
                        :disabled="!previous_page"
                        @click="pindahHalaman(previous_page)"
                    >
                        <i class="fa fa-chevron-left"></i> Sebelumnya
                    </button>
                    <button
                        class="btn btn-outline-secondary"
                        :disabled="!next_page"
                        @click="pindahHalaman(next_page)"
                    >
                        Berikutnya <i class="fa fa-chevron-right"></i>
                    </button>
                </div>
            </div>
        </template>

        <p v-else class="text-muted mb-0">
            <i class="fa fa-inbox me-1"></i>
            Tidak ada CPPT {{ label_jenis }} untuk pasien ini.
        </p>
    </div>
</template>

<script>
export default {
    name: "ResumeMedisCppt",

    props: {
        medrec: {
            type: [String, Number],
            required: true,
        },
        regno: {
            type: [String, Number],
            default: null,
        },
        /** "rajal", "ranap", atau "semua" — dipilih dari daftar Resume Medis. */
        jenis: {
            type: String,
            default: "rajal",
        },
    },

    data() {
        return {
            page: 1,
            rows: [],
            count: 0,
            next_page: false,
            previous_page: false,
            is_loading: false,
            is_exporting: false,
        };
    },

    computed: {
        label_jenis() {
            return { ranap: "rawat inap", semua: "", rajal: "rawat jalan" }[this.jenis];
        },
    },

    mounted() {
        this.getCppt();
    },

    methods: {
        getCppt() {
            this.is_loading = true;

            this.axios
                .get("/api/riwayat-pasien/cppt", {
                    params: {
                        medrec: this.medrec,
                        regno: this.regno,
                        jenis: this.jenis,
                        page: this.page,
                    },
                })
                .then(({ data }) => {
                    const cppt = (data.data || {}).cppt || {};

                    this.rows = cppt.data || [];
                    this.count = cppt.count || 0;
                    this.next_page = cppt.next_page || false;
                    this.previous_page = cppt.previous_page || false;
                })
                .catch(() => {
                    this.rows = [];
                    this.count = 0;
                    this.next_page = false;
                    this.previous_page = false;
                })
                .finally(() => (this.is_loading = false));
        },

        pindahHalaman(page) {
            this.page = page;
            this.getCppt();
        },

        /** Isi PDF-nya sama persis dengan daftar di layar, hanya tanpa paginasi. */
        unduhPdf() {
            this.is_exporting = true;

            this.axios
                .get("/api/riwayat-pasien/cppt-pdf", {
                    params: { medrec: this.medrec, regno: this.regno, jenis: this.jenis },
                    responseType: "blob",
                })
                .then(({ data }) => {
                    const url = URL.createObjectURL(data);
                    const tautan = document.createElement("a");

                    tautan.href = url;
                    tautan.download = `CPPT ${this.jenis} - ${this.medrec}.pdf`;
                    tautan.click();

                    URL.revokeObjectURL(url);
                })
                .catch(() =>
                    this.$swal({
                        icon: "warning",
                        title: "Gagal membuat PDF",
                        text: "Riwayat CPPT tidak dapat dicetak.",
                    })
                )
                .finally(() => (this.is_exporting = false));
        },

        uraian(row) {
            return [
                { kode: "S", isi: row.Subjective },
                { kode: "O", isi: row.Objective },
                { kode: "A", isi: row.Assessment },
                { kode: "P", isi: row.Planning },
            ];
        },

        instruksi(row) {
            return [
                row.Penunjang,
                row.IntuksiTendakaKesehatan,
                row.instruksi_dokter_spesialis,
                row.Konsul,
            ]
                .map((bagian) => (bagian || "").trim())
                .filter(Boolean)
                .join("\n");
        },

        unit(row) {
            if (row.JenisPasien === "RI") return "Rawat Inap";
            if (row.JenisPasien === "RJ") return "Rawat Jalan";

            return row.Instalasi == 2 ? "Rawat Inap" : "Rawat Jalan";
        },

        formatTanggal(value) {
            if (!value) return "-";
            return moment(String(value)).format("DD-MM-YYYY");
        },

        formatJam(value) {
            if (!value) return "";
            return moment(String(value)).format("HH:mm:ss");
        },
    },
};
</script>

<style scoped>
.cppt-table {
    font-size: 0.875rem;
    background: #fff;
}

.cppt-table thead th {
    vertical-align: middle;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}

.soap-kode {
    width: 34px;
    background: #f8f9fa;
}

.pre-wrap {
    white-space: pre-wrap;
}
</style>
